import React, { useState, useContext, useRef, useEffect } from "react";
// Context
import { CustomerContext } from "../data/customer/CustomerContext";
import { useData } from "../data/general/DataContext";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { getCities, getDistricts } from "../data/general/LocationSlice";
// Components
import TablePage from "../core/TablePage";
import CustomerAddModal from "../modals/customer/CustomerAddModal";
import CustomerUpdateModal from "../modals/customer/CustomerUpdateModal";
// UI
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { FaTrash, FaPencilAlt } from "react-icons/fa";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

function CustomerPage() {
  const { customers, addCustomer, updateCustomer, deleteCustomer } = useContext(CustomerContext);
  const { windowMode } = useData();
  const dispatch = useDispatch();

  const { cities, districts } = useSelector((state) => state.location);

  const [selectedCustomerType, setSelectedCustomerType] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const [searchedCity, setSearchedCity] = useState(""); 

  // Filtre state
  const [columnFilters, setColumnFilters] = useState({
    city: null,
    district: null,
    address: null,
  });

  // Şehirleri yükle
  useEffect(() => {
    dispatch(getCities());
  }, [dispatch]);

  // Seçilen şehre göre ilçeleri yükle
  useEffect(() => {
    if (columnFilters.city) {
      dispatch(getDistricts({ city: columnFilters.city }));
    }
  }, [columnFilters.city, dispatch]);

  // Web için Action Menu
  const ActionMenu = ({ rowData }) => {
    const op = useRef(null);
    if (windowMode === "mobile") return null;

    return (
      <>
        <Button
          label="..."
          className="p-button-rounded p-button-text"
          style={{ minWidth: "40px", fontSize: "22px", fontWeight: "bold" }}
          onClick={(e) => op.current.toggle(e)}
        />
        <OverlayPanel ref={op} dismissable showCloseIcon={false} style={{ padding: "0.5rem", left: "-5px" }}>
          <div className="flex flex-col gap-0 p-2 w-32">
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="p-button-text w-full justify-start"
              style={{ fontSize: "14px", gap: "10px" }}
              onClick={() => {
                setSelectedCustomer(rowData);
                setShowUpdateModal(true);
                op.current.hide();
              }}
            />
            <div className="w-full border-t border-gray-300 my-1"></div>
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="p-button-text w-full justify-start"
              style={{ color: "red", fontSize: "14px", gap: "10px" }}
              onClick={() => {
                deleteCustomer(rowData.id);
                op.current.hide();
              }}
            />
          </div>
        </OverlayPanel>
      </>
    );
  };


  // Table Columns
  const columns = [
    { field: "id", header: "ID", hideInFilter: true },
    { field: "fullName", header: "Ad Soyad" },
    { field: "phone1", header: "Telefon", hideInFilter: true },
    {
      field: "address",
      header: "Adres",
      body: (rowData) => (
        <div className="flex flex-col leading-tight">
          <span >{rowData.address}</span>
          <span className="text-xs text-gray-500">{rowData.city} / {rowData.district}</span>
        </div>
      ),
    },
    { field: "customerType", header: "Müşteri Türü" },
    {
      header: "İşlemler",
      body: (rowData) =>
        windowMode === "mobile" ? (
          <div className="flex gap-2 mt-2 w-full">
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="flex-1"
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                height: "26px",      
                fontSize: "10px",    
                justifyContent: "center",
                gap: "4px",
                padding: "0 6px",    
              }}
              onClick={() => {
                setSelectedCustomer(rowData);
                setShowUpdateModal(true);
              }}
            />
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="flex-1"
              style={{
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                height: "26px",      
                fontSize: "10px", 
                justifyContent: "center",
                gap: "4px",
                padding: "0 6px",    
              }}
              onClick={() => deleteCustomer(rowData.id)}
            />
          </div>
        ) : (
          <ActionMenu rowData={rowData} />
        ),
      hideInFilter: true,
    },
  ];



  const enhancedColumns = columns.map((col) =>
    col.field === "id" ? { ...col, style: { width: "60px", padding: "0 12px" } } : { ...col, style: { padding: "0 12px" } }
  );

  // Filtre paneli için custom component 
  const LocationFilter = () => (
    <div className="flex flex-col gap-2">
      <Autocomplete
        options={cities || []}
        value={columnFilters.city || ""}
        onChange={(e, newValue) => {
          setColumnFilters({ ...columnFilters, city: newValue, district: "" });
          if (newValue && newValue !== searchedCity) {
            dispatch(getDistricts({ city: newValue }));
            setSearchedCity(newValue);
          }
        }}
        getOptionLabel={(option) => option}
        renderOption={(props, option) => (
          <li {...props} key={option} className="p-2 hover:bg-gray-100 cursor-pointer">
            {option}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Şehir seçin" size="small" />}
        disablePortal
        autoHighlight
        style={{ width: 200 }}
      />

      <Autocomplete
        options={districts || []}
        value={columnFilters.district || ""}
        onChange={(e, newValue) => setColumnFilters({ ...columnFilters, district: newValue })}
        getOptionLabel={(option) => option}
        renderOption={(props, option) => (
          <li {...props} key={option} className="p-2 hover:bg-gray-100 cursor-pointer">
            {option}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="İlçe seçin" size="small" />}
        disablePortal
        autoHighlight
        disabled={!columnFilters.city}
        style={{ width: 200 }}
      />
    </div>
  );

  // columnFilterOptions
  const columnFilterOptions = {
    customerType: {
      type: "checkbox",
      options: ["Bireysel", "Kurumsal"],
      value: selectedCustomerType,
      onChange: setSelectedCustomerType
    },
    fullName: { type: "text" },
    address: {
      type: "custom",
      body: LocationFilter
    }
  };

  // Filtrelenmiş veri
  const filteredCustomers = customers.filter((c) =>
    (!columnFilters.city || c.city === columnFilters.city) &&
    (!columnFilters.district || c.district === columnFilters.district) &&
    (!selectedCustomerType.length || selectedCustomerType.includes(c.customerType)) &&
    (!searchTerm || c.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <div className="p-6">
       <TablePage
  title="Müşteri Listesi"
  subTitle="Müşteri ekleyebilir, düzenleyebilir veya silebilirsiniz."
  columns={enhancedColumns}
  data={filteredCustomers}
  defaultRows={10}
  rowsPerPageOptions={[10, 15, 25, 50,100]}
  showSearch
  searchValue={searchTerm}
  onSearchChange={(e) => setSearchTerm(e.target.value)}
  onAdd={() => setShowAddModal(true)}
  columnFilterOptions={columnFilterOptions}
  // 🔹 Satır rengini müşteri türüne göre ayarlıyoruz
  rowClassName={(rowData) =>
    rowData.customerType === "Kurumsal"
      ? "row-kurumsal" 
      : rowData.customerType === "Bireysel"
      ? "row-bireysel" 
      : ""
  }
/>
      </div>

      <CustomerAddModal
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        cities={cities}            
        districts={districts}      
        onSubmit={(customer) => addCustomer(customer)}
      />

      <CustomerUpdateModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        customer={selectedCustomer}
        cities={cities}
        districts={districts}
        onSubmit={(customer) => updateCustomer(customer)}
      />
    </>
  );
}

export default CustomerPage;
